/** Fullscreen image viewer - "the normal stuff": highest resolution, ←/→
 *  arrows (buttons and keyboard), Escape or backdrop-click to close, and a
 *  counter. Shared by the Galerie and the shop's product pages. */

import { useCallback, useEffect } from 'react'
import { mediaSrcSet, mediaUrl } from '../lib/api'
import { useLocale } from '../lib/hooks'
import { fill, t } from '../lib/i18n'

export interface LightboxItem {
  filename: string
  alt?: string
  title?: string
  subtitle?: string
}

export default function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
}: {
  items: LightboxItem[]
  index: number
  onClose: () => void
  onNavigate: (next: number) => void
}) {
  const locale = useLocale()
  const strings = t(locale).gallery
  const item = items[index]

  const go = useCallback(
    (delta: number) => onNavigate((index + delta + items.length) % items.length),
    [index, items.length, onNavigate],
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') go(1)
      if (event.key === 'ArrowLeft') go(-1)
    }
    document.addEventListener('keydown', onKey)
    // Stop the page behind the overlay from scrolling.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [go, onClose])

  if (!item) return null

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={item.title || item.alt}
      data-testid="lightbox"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={strings.close}
        data-testid="lightbox-close"
        className="absolute right-4 top-4 p-3 text-white/80 transition-colors hover:text-white"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      {items.length > 1 ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            go(-1)
          }}
          aria-label={strings.previous}
          data-testid="lightbox-prev"
          className="absolute left-2 top-1/2 -translate-y-1/2 p-4 text-white/70 transition-colors hover:text-white md:left-8"
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      ) : null}

      <figure className="max-h-full max-w-[1100px]" onClick={(e) => e.stopPropagation()}>
        <img
          src={mediaUrl(item.filename)}
          srcSet={mediaSrcSet(item.filename)}
          sizes="(max-width: 1100px) 100vw, 1100px"
          alt={item.alt || item.title || ''}
          data-testid="lightbox-image"
          className="mx-auto max-h-[78vh] w-auto object-contain"
        />
        <figcaption className="pt-4 text-center font-serif text-white">
          {item.title ? (
            <span className="block text-[26px] leading-[1.5]">{item.title}</span>
          ) : null}
          {item.subtitle ? (
            <span className="block text-[18px] text-white/70">{item.subtitle}</span>
          ) : null}
          {items.length > 1 ? (
            <span className="mt-2 block font-sans text-[12px] uppercase tracking-[2px] text-white/50">
              {fill(strings.imageCounter, { i: index + 1, n: items.length })}
            </span>
          ) : null}
        </figcaption>
      </figure>

      {items.length > 1 ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            go(1)
          }}
          aria-label={strings.next}
          data-testid="lightbox-next"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-4 text-white/70 transition-colors hover:text-white md:right-8"
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      ) : null}
    </div>
  )
}
